import { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumbs from "../../components/General/BreadCrumbs";
import SidebarBox from "../../components/General/SidebarBox";
import LoadingSkeleton from "../../components/UI/LoadingSkeleton";
import Modal from "../../components/UI/Modal";
import Button from "../../components/UI/Button";
import BrandImage from "../../components/Vehicles/BrandImage";
import FindVehicleFilter from "../../components/Vehicles/FindVehicleFilter";
import SingleNewVehicleClass from "../../components/Vehicles/SingleNewVehicleClass";
import SortVehicles from "../../components/Vehicles/SortVehicles";
import {
  BrandI,
  getNewVehiclesOfSingleBrand,
  getNewVehiclesOfSingleBrandRet,
  getSingleBrand,
} from "../../utils/fetcher";
import { VehicleNames } from "../../utils/GeneralTypes";
import { useScrollToTop } from "../../utils/generalUtils";
import style from "./index.module.css";
import { useTranslation } from "../../hooks/useTranslation";
import EmptyContent from "../../components/General/EmptyContent";
import { useParams } from "react-router-dom";

type componentDataT = {
  brand?: BrandI | null;
  models?: getNewVehiclesOfSingleBrandRet["models"] | null;
  extraBreadCrumbs: { text: string; link: string }[];
};

const NewVehiclesBrands = ({ vehicleNames }: ComponentPropsType) => {
  const { brandID } = useParams<{ brandID: string }>();
  const { locale } = useTranslation();

  const initialMount = useRef<boolean>(true);

  useScrollToTop();

  const [isComparingModalActive, setIsComparingModalActive] = useState<boolean>(false);

  const [componentData, setComponentData] = useState<{
    isFetching: boolean;
    data: componentDataT;
  }>({
    isFetching: true,
    data: {
      brand: null,
      models: null,
      extraBreadCrumbs: [],
    },
  });

  const [sortData, setSortData] = useState<{
    price: "asc" | "desc" | "none";
    year: "asc" | "desc" | "none";
  }>({
    price: "none",
    year: "none",
  });

  useEffect(() => {
    if (!initialMount.current) {
      setComponentData((current) => ({
        ...current,
        isFetching: true,
      }));
    }
    const data: componentDataT = {
      brand: null,
      models: null,
      extraBreadCrumbs: [],
    };
    (async () => {
      const res = await Promise.all([
        getNewVehiclesOfSingleBrand(vehicleNames.single, +brandID),
        ...(initialMount.current ? [getSingleBrand(+brandID)] : []),
      ]);
      data.models = res[0].data.models;
      if (initialMount.current) {
        data.brand = res[1].data.brand;
        if (data.brand) {
          data.extraBreadCrumbs = [];
          data.extraBreadCrumbs.push({ text: data.brand.name, link: "" });
        }
      }

      setComponentData((current) => ({
        isFetching: false,
        data: {
          ...current.data,
          ...data,
        },
      }));
      initialMount.current = false;
    })();
  }, [brandID, vehicleNames]);

  const viewSortedData = useMemo(() => {
    if (sortData.year === "none" && sortData.price === "none") {
      return componentData.data.models ?? [];
    } else {
      let sortName = sortData.year !== "none" ? "year" : "price";
      if (!componentData.data.models) return [];
      return componentData.data.models.sort((a: any, b: any) =>
        sortName === "price"
          ? sortData.price === "asc"
            ? +a.vehicles[0].price - +b.vehicels[0].price
            : +b.vehicles[0].price - +a.vehicles[0].price
          : sortData.year === "asc"
          ? a[sortName] - b[sortName]
          : b[sortName] - a[sortName],
      );
    }
  }, [sortData, componentData.data]);

  return (
    <>
      {isComparingModalActive ? (
        <Modal onClose={() => setIsComparingModalActive(false)}>
          <div className={style.modal}>
            <p className={style.modal_title}>
              {locale("compare_list_full")} {locale(vehicleNames.single).toLocaleLowerCase()}
            </p>
            <div className={style.modal_ctas}>
              <Button styleType="primary_black" onClick={() => setIsComparingModalActive(false)}>
                {locale("cancel")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
      {/* <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <BreadCrumbs
              pages={[
                { text: locale("home"), link: "/" },
                {
                  text: `${locale("new")} ${locale(vehicleNames.plural)}`,
                  link: `/distributor/${vehicleNames.plural}`,
                },
                ...(componentData.data.extraBreadCrumbs.length > 0
                  ? componentData.data.extraBreadCrumbs
                  : [{ text: "", link: "" }]),
              ]}
              isLoading={componentData.isFetching}
            />
          </div>
        </div>
      </div> */}
      <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <div className={`${style.page_row} row`}>
              <div className="col-lg-4">
                <SidebarBox>
                  <FindVehicleFilter
                    vehicleNames={vehicleNames}
                    vehicleCondition="new"
                    searchBaseUrl={`/distributor/${vehicleNames.plural}/find`}
                  />
                </SidebarBox>
              </div>
              <div className="col-lg-8">
                {!componentData.isFetching && componentData.data.models!.length === 0 ? (
                  <div className="col-12">
                    <EmptyContent>
                      <p>{locale("noResult")}</p>
                    </EmptyContent>
                  </div>
                ) : null}
                {((!componentData.isFetching && componentData.data.models!.length > 0) || componentData.isFetching) &&
                  (componentData.isFetching ? (
                    <LoadingSkeleton count={8} />
                  ) : (
                    <>
                      <BrandImage
                        image={componentData.data.brand!.logo}
                        link={`/new-${vehicleNames.plural}/brands/${componentData.data.brand!.id}`}
                        name={componentData.data.brand!.name}
                        hasLink={false}
                        styleobject={{ marginBottom: 0 }}
                      />
                      <SortVehicles
                        setSortData={(name: string, value: string) =>
                          setSortData({
                            year: "none",
                            price: "none",
                            [name]: value,
                          })
                        }
                        groups={[
                          {
                            name: locale("price"),
                            buttons: [
                              {
                                active: sortData.price === "asc",
                                name: ["price", "asc"],
                              },
                              {
                                active: sortData.price === "desc",
                                name: ["price", "desc"],
                              },
                            ],
                          },
                          {
                            name: locale("year"),
                            buttons: [
                              {
                                active: sortData.year === "asc",
                                name: ["year", "asc"],
                              },
                              {
                                active: sortData.year === "desc",
                                name: ["year", "desc"],
                              },
                            ],
                          },
                        ]}
                      />
                      {viewSortedData.map((item, index) => (
                        <div className={style.single_vehicle} key={index}>
                          {index !== 0 && (
                            <div className={`${style.separator} section_content_padding`}>
                              <div className="section_separator"></div>
                            </div>
                          )}
                          <SingleNewVehicleClass
                            setIsComparingModalActive={setIsComparingModalActive}
                            vehicleNames={vehicleNames}
                            classesNo={item.vehicles.length}
                            baseLink={`/distributor/${vehicleNames.plural}/${vehicleNames.single}`}
                            name={item.name}
                            price={item.vehicles[0].price.toString()}
                            image={item.image}
                            year={item.year}
                            classes={item.vehicles.map((el) => ({
                              id: el[`vehicle_id`],
                              name: el.class_name,
                              price: el.price.toString(),
                              // TODO: enable it when we have compatible types
                              // selling_flag: el.selling_flag,
                            }))}
                          />
                        </div>
                      ))}
                    </>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewVehiclesBrands;

type ComponentPropsType = {
  vehicleNames: VehicleNames;
};
